/**
 * Tanda Mulai, the launch gate. The splash in index.html is already painted when this runs;
 * the gate decides when it leaves:
 *
 *   release = race( all(worker ready, aggregates ready, min 400 ms), cap 1500 ms )
 *
 * with three exceptions: another tab holds the database (the second-tab screen replaces
 * the shell, released after the minimum), the worker fails (same), and a first install
 * (snapshot pages arrive: the tick gives way to a 2 px ink bar and the cap no longer
 * applies, the splash leaves when the snapshot ends). performance.mark at splash-start
 * (index.html), worker-ready, aggregates-ready and release; the measure `satu:splash` is
 * the number reported against the 1.5 s budget.
 *
 * `runGate` is pure apart from the injected timers so it can be tested under Node; the DOM
 * side (the install bar, the FLIP of the mark onto the header mark) lives in ./splash.ts.
 */
import type { DbLockState } from '../../db/lock'
import type { SnapshotProgress } from '../../db/protocol'

export interface GateClient {
  readonly ready: Promise<unknown>
  readonly lockState: DbLockState
  requestRaw(type: 'aggregates:get', payload: unknown): Promise<unknown>
  onLockState(listener: (state: DbLockState) => void): () => void
  onSnapshotProgress(listener: (progress: SnapshotProgress) => void): () => void
}

/**
 * Why the splash left: data ready, the cap, another tab, a failure, a finished first install,
 * or a signed-out device sent straight to /masuk (main.tsx) without waiting for the database.
 */
export type GateReason = 'siap' | 'batas' | 'tab_lain' | 'gagal' | 'unduhan' | 'masuk'

export interface GateResult {
  reason: GateReason
  elapsedMs: number
}

export interface GateOptions {
  client: GateClient
  minMs?: number
  capMs?: number
  /** performance.now() of the splash-start mark; defaults to now. */
  startedAt?: number
  now?: () => number
  setTimeout?: (fn: () => void, ms: number) => unknown
  clearTimeout?: (handle: unknown) => void
  mark?: (name: string, detail?: unknown) => void
  /** First-install progress while the splash is up. */
  onInstall?: (progress: SnapshotProgress) => void
}

export const SPLASH_START_MARK = 'satu:splash-start'
export const WORKER_READY_MARK = 'satu:worker-ready'
export const AGGREGATES_READY_MARK = 'satu:aggregates-ready'
export const SPLASH_RELEASE_MARK = 'satu:splash-release'
export const SPLASH_MEASURE = 'satu:splash'
export const SPLASH_MIN_MS = 400
export const SPLASH_CAP_MS = 1500

function defaultMark(name: string, detail?: unknown): void {
  const perf = globalThis.performance as
    | { mark?: (n: string, o?: { detail: unknown }) => unknown }
    | undefined
  perf?.mark?.(name, { detail })
}

export function runGate(options: GateOptions): Promise<GateResult> {
  const now = options.now ?? (() => performance.now())
  const schedule = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms))
  const cancel =
    options.clearTimeout ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>))
  const mark = options.mark ?? defaultMark
  const t0 = options.startedAt ?? now()
  const minMs = options.minMs ?? SPLASH_MIN_MS
  const capMs = options.capMs ?? SPLASH_CAP_MS
  const { client } = options

  return new Promise<GateResult>((resolve) => {
    let settled = false
    let installing = false
    const unsubs: Array<() => void> = []
    const handles: unknown[] = []

    const sleep = (ms: number) =>
      new Promise<void>((done) => {
        handles.push(schedule(done, ms))
      })

    const finish = (reason: GateReason) => {
      if (settled) return
      settled = true
      for (const unsub of unsubs) unsub()
      for (const handle of handles) cancel(handle)
      const elapsedMs = now() - t0
      mark(SPLASH_RELEASE_MARK, { reason, elapsedMs })
      resolve({ reason, elapsedMs })
    }

    const minHold = sleep(Math.max(0, minMs - (now() - t0)))
    const workerReady = client.ready.then(() => {
      mark(WORKER_READY_MARK)
    })
    const aggregatesReady = client.ready
      .then(() => client.requestRaw('aggregates:get', {}))
      .then(
        () => mark(AGGREGATES_READY_MARK),
        // Reserved until section 06 registers the handler: not a failure of the launch.
        (error: unknown) => mark(AGGREGATES_READY_MARK, { skipped: String(error) }),
      )

    Promise.all([workerReady, aggregatesReady, minHold]).then(
      () => {
        // A first install is under way: the snapshot end releases the splash instead.
        if (!installing) finish('siap')
      },
      () => {
        minHold.then(() => finish('gagal'))
      },
    )

    sleep(capMs).then(() => {
      if (!installing) finish('batas')
    })

    const onLock = (state: DbLockState) => {
      if (state.status === 'dipegang_tab_lain') minHold.then(() => finish('tab_lain'))
    }
    unsubs.push(client.onLockState(onLock))
    onLock(client.lockState)

    unsubs.push(
      client.onSnapshotProgress((progress) => {
        if (settled) return
        options.onInstall?.(progress)
        if (progress.phase === 'mulai' || progress.phase === 'tabel') {
          installing = true
          return
        }
        installing = false
        Promise.all([workerReady, minHold]).then(
          () => finish('unduhan'),
          () => finish('gagal'),
        )
      }),
    )
  })
}

/** "38 %" from received rows over the server's count; hidden when the total is unknown. */
export function installPercent(progress: SnapshotProgress): number | null {
  const total = progress.total ?? null
  const received = progress.received ?? 0
  if (total === null || total <= 0) return null
  return Math.max(0, Math.min(100, Math.round((received / total) * 100)))
}
